import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { BehaviorSubject, distinctUntilChanged, map, Subscription } from 'rxjs';
import { IContributionApi } from '../api/interfaces/contribution-abstract.api';
import { NEVER_TOKEN } from '../constants/injection-tokens';
import { Contribution } from '../models/contribution.model';
import { AbstractFacade } from './abstract.facade';

// 3 minutes
const CONTRIBUTION_CACHE_VALIDITY = 300000;

interface ContributionState {
  contribution: Contribution;
  loading: boolean;
  loaded: boolean;
  lastLoadingTime: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class ContributionFacade extends AbstractFacade implements OnDestroy {
  private state: ContributionState = {
    contribution: null,
    loading: false,
    loaded: false,
    lastLoadingTime: null
  };
  public store = new BehaviorSubject<ContributionState>(this.state);
  public state$ = this.store.asObservable();

  public readonly contribution$ = this.state$.pipe(map(state => state.contribution), distinctUntilChanged());
  public readonly loading$ = this.state$.pipe(map(state => state.loading), distinctUntilChanged());
  private subscriptions: Subscription;

  constructor(
    private readonly contributionApi: IContributionApi,
    @Inject(NEVER_TOKEN) @Optional() protected readonly userId: number
  ) {
    super();
  }

  getState$() {
    return this.state$;
  }

  setState(newState: Partial<ContributionState>) {
    this.state = {
      ...this.state,
      ...newState
    };
    this.store.next(this.state);
  }

  load() {
    if (this.state.loading) {
      return;
    }
    if (this.state.loaded || this.isCacheValid()) {
      return;
    }
    this.setState({
      loading: true,
    });
    this.subscriptions = this.contributionApi.getContributionSummary(this.userId).subscribe((contribution) => {
      this.setState({
        contribution,
        loading: false,
        loaded: true,
        lastLoadingTime: Date.now(),
      });
    });
  }

  private isCacheValid() {
    if (!this.state.lastLoadingTime) {
      return false;
    }
    const currentTime = Date.now();
    if (currentTime > this.state.lastLoadingTime + CONTRIBUTION_CACHE_VALIDITY) {
      return false;
    }
    return true;
  }

  isLoaded() {
    return this.state.loaded;
  }

  ngOnDestroy(): void {
      this.subscriptions?.unsubscribe();
  }
}
